import { useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { ColorModeContext } from '../contexts/ColorModeContext';
import { palette } from '../theme/colors';
import { SubscriptionTier, TIER_NAMES, UpgradeScenario, UPGRADE_SCENARIOS } from '../types/subscription';

interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
  feature?: string;
  requiredTier?: SubscriptionTier;
  currentTier?: SubscriptionTier;
  customMessage?: string;
  scenario?: UpgradeScenario;
}

interface TierFeature {
  name: string;
  free: boolean | string;
  starter: boolean | string;
  professional: boolean | string;
  enterprise: boolean | string;
}

const TIER_FEATURES: TierFeature[] = [
  { name: 'Products', free: '50 max', starter: '500 max', professional: '2,000 max', enterprise: 'Unlimited' },
  { name: 'Clients', free: '100 max', starter: 'Unlimited', professional: 'Unlimited', enterprise: 'Unlimited' },
  { name: 'Sales/hour', free: '10 max', starter: 'Unlimited', professional: 'Unlimited', enterprise: 'Unlimited' },
  { name: 'Locations', free: '1 virtual', starter: '5 max', professional: '20 max', enterprise: 'Unlimited' },
  { name: 'Dashboards', free: false, starter: true, professional: true, enterprise: true },
  { name: 'Reservations', free: false, starter: true, professional: true, enterprise: true },
  { name: 'B2B (Quotes/Invoices)', free: false, starter: false, professional: true, enterprise: true },
  { name: 'Point of Sale', free: false, starter: true, professional: true, enterprise: true },
  { name: 'Loyalty Program', free: false, starter: true, professional: true, enterprise: true },
  { name: 'Delivery & Logistics', free: false, starter: false, professional: false, enterprise: true },
  { name: 'Advanced Analytics', free: false, starter: false, professional: true, enterprise: true },
  { name: 'Multi-location Management', free: false, starter: false, professional: false, enterprise: true },
];

const TIER_PRICES: Record<SubscriptionTier, string> = {
  free: '$0',
  starter: '$29/mo',
  professional: '$99/mo',
  enterprise: '$299/mo',
};

export const UpgradeModal = ({
  open,
  onClose,
  feature,
  requiredTier = 'professional',
  currentTier = 'free',
  customMessage,
  scenario,
}: UpgradeModalProps) => {
  const { mode } = useContext(ColorModeContext);

  // Use scenario config if provided
  const scenarioConfig = scenario ? UPGRADE_SCENARIOS[scenario] : null;
  const effectiveFeature = scenarioConfig?.featureName || feature;
  const effectiveRequiredTier = scenarioConfig?.requiredTier || requiredTier;
  const effectiveMessage = scenarioConfig?.message || customMessage;
  const effectiveTitle = scenarioConfig?.title || (effectiveFeature ? `Unlock ${effectiveFeature}` : 'Upgrade Your Plan');

  const colors = mode === 'dark' ? {
    surface: palette.surface,
    border: palette.border,
    textPrimary: palette.textPrimary,
    textMuted: palette.textMuted,
    accent: palette.accent,
  } : {
    surface: palette.light.surface,
    border: palette.light.border,
    textPrimary: palette.light.textPrimary,
    textMuted: palette.light.textMuted,
    accent: palette.accent,
  };

  const renderFeatureCell = (value: boolean | string) => {
    if (typeof value === 'string') {
      return <Typography variant="body2">{value}</Typography>;
    }
    return value ? (
      <CheckCircleIcon sx={{ color: colors.accent, fontSize: 20 }} />
    ) : (
      <Typography variant="body2" color="text.disabled">-</Typography>
    );
  };

  const handleUpgrade = (tier: SubscriptionTier) => {
    // TODO: Integrate with payment/billing system
    // For now, just close the modal
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: colors.surface,
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockIcon sx={{ color: colors.accent }} />
          <Typography variant="h6">
            {effectiveTitle}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {(effectiveMessage || effectiveFeature) && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {effectiveMessage || `${effectiveFeature} requires ${effectiveRequiredTier === 'enterprise' ? 'an' : 'a'} ${TIER_NAMES[effectiveRequiredTier]} subscription or higher.`}
          </Typography>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Feature</TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="subtitle2">{TIER_NAMES.free}</Typography>
                    <Typography variant="caption" color="text.secondary">{TIER_PRICES.free}</Typography>
                    {currentTier === 'free' && (
                      <Chip label="Current" size="small" color="default" sx={{ mt: 0.5 }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell align="center" sx={{ bgcolor: effectiveRequiredTier === 'starter' ? 'action.selected' : 'inherit' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: effectiveRequiredTier === 'starter' ? 'bold' : 'normal' }}>
                      {TIER_NAMES.starter}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{TIER_PRICES.starter}</Typography>
                    {currentTier === 'starter' && (
                      <Chip label="Current" size="small" color="primary" sx={{ mt: 0.5 }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell align="center" sx={{ bgcolor: effectiveRequiredTier === 'professional' ? 'action.selected' : 'inherit' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: effectiveRequiredTier === 'professional' ? 'bold' : 'normal' }}>
                      {TIER_NAMES.professional}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{TIER_PRICES.professional}</Typography>
                    {currentTier === 'professional' && (
                      <Chip label="Current" size="small" color="primary" sx={{ mt: 0.5 }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell align="center" sx={{ bgcolor: effectiveRequiredTier === 'enterprise' ? 'action.selected' : 'inherit' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: effectiveRequiredTier === 'enterprise' ? 'bold' : 'normal' }}>
                      {TIER_NAMES.enterprise}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{TIER_PRICES.enterprise}</Typography>
                    {currentTier === 'enterprise' && (
                      <Chip label="Current" size="small" color="secondary" sx={{ mt: 0.5 }} />
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {TIER_FEATURES.map((tier) => (
                <TableRow key={tier.name}>
                  <TableCell>{tier.name}</TableCell>
                  <TableCell align="center">{renderFeatureCell(tier.free)}</TableCell>
                  <TableCell align="center" sx={{ bgcolor: effectiveRequiredTier === 'starter' ? 'action.hover' : 'inherit' }}>
                    {renderFeatureCell(tier.starter)}
                  </TableCell>
                  <TableCell align="center" sx={{ bgcolor: effectiveRequiredTier === 'professional' ? 'action.hover' : 'inherit' }}>
                    {renderFeatureCell(tier.professional)}
                  </TableCell>
                  <TableCell align="center" sx={{ bgcolor: effectiveRequiredTier === 'enterprise' ? 'action.hover' : 'inherit' }}>
                    {renderFeatureCell(tier.enterprise)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} color="inherit">
          Maybe Later
        </Button>
        {/* Show upgrade button for the required tier */}
        {currentTier === 'free' && effectiveRequiredTier !== 'free' && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<RocketLaunchIcon />}
            onClick={() => handleUpgrade(effectiveRequiredTier)}
          >
            Upgrade to {TIER_NAMES[effectiveRequiredTier]}
          </Button>
        )}
        {currentTier === 'starter' && (effectiveRequiredTier === 'professional' || effectiveRequiredTier === 'enterprise') && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<RocketLaunchIcon />}
            onClick={() => handleUpgrade(effectiveRequiredTier)}
          >
            Upgrade to {TIER_NAMES[effectiveRequiredTier]}
          </Button>
        )}
        {currentTier === 'professional' && effectiveRequiredTier === 'enterprise' && (
          <Button
            variant="contained"
            color="secondary"
            startIcon={<RocketLaunchIcon />}
            onClick={() => handleUpgrade('enterprise')}
          >
            Upgrade to {TIER_NAMES.enterprise}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UpgradeModal;
